// =====================================================================================
// INVRT Radar — web console — the guided loop
//
// The `AgentLoop` that ships tonight. Deterministic end to end: it routes with the rule
// matcher and answers with a fixed step per intent. No model, no network call of its own.
//
// Every reply is written to be honest about what happens next, particularly around money:
// the loop can put a QUOTE in front of a person, and nothing else.
// =====================================================================================

import { matchIntent } from "./intents";
import type { AgentContext, AgentLoop, AgentTurn } from "./loop";

export const guidedLoop: AgentLoop = {
  mode: "Guided",
  modelDriven: false,

  send(input: string, context: AgentContext): Promise<AgentTurn> {
    const { intent } = matchIntent(input);
    const where = context.orgName ? ` for ${context.orgName}` : "";

    switch (intent) {
      case "find_people":
        return Promise.resolve({
          intent,
          steps: [
            {
              kind: "form",
              form: { kind: "find_people" },
              text: `Tell me who you are after${where} and I will show you the matches. Searching is free — you only spend credits when you approve a reveal.`,
            },
          ],
        });

      case "site_visitors":
        return Promise.resolve({
          intent,
          steps:
            context.siteCount === 0
              ? [
                  {
                    kind: "form",
                    form: { kind: "setup_site" },
                    text: "There is no site set up yet, so there is nothing to show. Add your domain and I will hand you the snippet to install.",
                  },
                ]
              : [
                  {
                    kind: "form",
                    form: { kind: "site_visitors" },
                    text: "Pick a site and I will show you who has been identified on it. Companies you already have are marked and never billed.",
                  },
                ],
        });

      case "setup_site":
        return Promise.resolve({
          intent,
          steps: [
            {
              kind: "form",
              form: { kind: "setup_site" },
              text: "Give me the domain and how consent is handled, and I will create the site and hand you the install snippet.",
            },
          ],
        });

      case "check_balance":
        return Promise.resolve({
          intent,
          steps: [{ kind: "balance", text: `Here is where the wallet stands${where}.` }],
        });

      case "pricing":
        return Promise.resolve({
          intent,
          steps: [
            {
              kind: "pricing",
              text: "This is your own price list. Use it to plan — the binding figure always comes from a quote, right before you approve it.",
            },
          ],
        });

      case "suppression":
        return Promise.resolve({
          intent,
          steps: [
            {
              kind: "suppression",
              text: "Your suppression list is how I know who you already have. Anyone on it is never billed to you, whether they turn up in a search or on your site.",
            },
          ],
        });

      case "help":
        return Promise.resolve({
          intent,
          steps: [
            {
              kind: "menu",
              text: "I find the people worth contacting, and I name the companies already on your site. Pick one of these, or just say what you want.",
            },
          ],
        });

      case "unknown":
      default:
        return Promise.resolve({
          intent: "unknown",
          steps: [
            {
              kind: "menu",
              text: "I did not follow that. Here is what I can do — pick one, or say it another way.",
            },
          ],
        });
    }
  },
};
